import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useEventCallback, useEventListener } from 'usehooks-ts'

import FacebookIcon from '@wwt/shared/assets/icons/logos/facebook.svg'
import InstagramIcon from '@wwt/shared/assets/icons/logos/instagram.svg'
import MessengerIcon from '@wwt/shared/assets/icons/logos/messenger.svg'
import WhatsAppIcon from '@wwt/shared/assets/icons/logos/whatsapp.svg'
import ShareIcon from '@wwt/shared/assets/icons/outline/share.svg'

import { cn } from '@/shared/lib/cn'
import { Button, ButtonProps } from '@/shared/ui/Button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover'

import { useShare } from '../lib/useShare'
import { CopyLink } from './ShareOptions/CopyLink'
import { MoreOptions } from './ShareOptions/MoreOptions'
import { ShareLink } from './ShareOptions/ShareLink'

interface ShareButtonProps extends ButtonProps {
	slots?: {
		base?: string
		content?: string
	}
}

export const ShareButton = ({
	slots,
	className,
	...props
}: ShareButtonProps) => {
	const { t } = useTranslation('header')

	const [open, setOpen] = useState(false)

	const {
		handleFacebookShare,
		handleInstagramShare,
		handleMessengerShare,
		handleWhatsappShare
	} = useShare()

	const handleViewportResize = useEventCallback(() => {
		if (window.innerWidth < 1280) {
			setOpen(false)
		}
	})

	useEventListener('resize', handleViewportResize)

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
		>
			<PopoverTrigger asChild>
				<Button
					className={cn(className, slots?.base)}
					variant="tertiary"
					size="icon-sm"
					data-wwt-id="header__share--button"
					{...props}
				>
					<ShareIcon />
					<span className="sr-only">{t('share.open')}</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className={cn('max-xl:hidden', slots?.content)}
				side="bottom"
				align="end"
			>
				<h4 className="text-foreground mb-4 text-xl font-medium">
					{t('share.title')}
				</h4>
				<ul className="grid grid-cols-3 gap-x-8 gap-y-4 *:h-6">
					<li>
						<CopyLink />
					</li>
					<li>
						<ShareLink
							icon={<MessengerIcon />}
							text={t('share.options.messenger')}
							data-wwt-id="header__share-messenger--link"
							onShare={handleMessengerShare}
						/>
					</li>
					<li>
						<ShareLink
							icon={<InstagramIcon />}
							text={t('share.options.instagram')}
							data-wwt-id="header__share-instagram--link"
							onShare={handleInstagramShare}
						/>
					</li>
					<li>
						<ShareLink
							icon={<WhatsAppIcon />}
							text={t('share.options.whatsapp')}
							data-wwt-id="header__share-whatsapp--link"
							onShare={handleWhatsappShare}
						/>
					</li>
					<li>
						<ShareLink
							icon={<FacebookIcon />}
							text={t('share.options.facebook')}
							data-wwt-id="header__share-facebook--link"
							onShare={handleFacebookShare}
						/>
					</li>
					<li>
						<MoreOptions />
					</li>
				</ul>
			</PopoverContent>
		</Popover>
	)
}
